/**
 * Dives (docs/spec/02 §5, §6): each launch draws a fresh path from where the enemy sits to where
 * the fighter is now, in the shape that kind of enemy flies.
 */

import { clamp, cos, deg, DOWN, PI, sin, UP } from './math';
import type { Path } from './path';
import { PathBuilder } from './path';
import { isFrenzy, makeEnemy } from './roster';
import type { Difficulty } from './tuning';
import { BEAM, BOMB, DIVE, FIELD, FORMATION } from './tuning';
import type { AfterPath, Enemy, GameState } from './types';

const BOTTOM = FIELD.h + 16;

function nextGroup(s: GameState): number {
  return s.director.nextGroup++;
}

/** Outward from the middle of the formation, now and then the other way. */
function peelSide(s: GameState, e: Enemy): number {
  const out = e.x >= FORMATION.cx ? 1 : -1;
  return s.rng.oneIn(4) ? -out : out;
}

/** Where the fighter is, give or take, and no nearer a wall than `margin` (room for a weave, or escorts). */
function aimX(s: GameState, margin = 20): number {
  return clamp(s.player.x + s.rng.int(49) - 24, margin, FIELD.w - margin);
}

function peel(e: Enemy, side: number, radius: number): PathBuilder {
  return new PathBuilder(e.x, e.y, UP).arc(side * PI, radius);
}

/**
 * Out through the bottom edge, inside the field, whatever slant the dive has left it with: a dive
 * across the screen must not slip out through a side wall and fly on unseen (02 §5).
 */
function exitBottom(b: PathBuilder): PathBuilder {
  const run = (BOTTOM - b.y) / Math.max(0.2, sin(b.h));
  const x = clamp(b.x + cos(b.h) * run, 16, FIELD.w - 16);
  return b.steerTo(x, BOTTOM, 36, 2);
}

/** Swoop to the fighter's row, loop toward the middle and stop there, nose up: it homes from below. */
function stingerPath(s: GameState, e: Enemy): Path {
  const b = peel(e, peelSide(s, e), DIVE.peelRadius).steerTo(aimX(s), 228, 44, 4);
  const turn = b.x > FORMATION.cx ? 1 : -1;
  return b.arc(turn * deg(200), 18).build();
}

/** Swoop, then weave about the vertical (so it never wanders off through a wall) and out through the bottom. */
function mothPath(s: GameState, e: Enemy): Path {
  const side = peelSide(s, e);
  return exitBottom(
    peel(e, side, DIVE.peelRadius)
      .steerTo(aimX(s, 30), 168, 44, 4)
      .turnTo(DOWN - side * deg(40), 24)
      .turnTo(DOWN + side * deg(40), 24)
      .turnTo(DOWN, 24),
  ).build();
}

/** A wider, statelier swoop than the Moth's, with room for escorts either side. */
function monarchPath(s: GameState, e: Enemy): Path {
  const side = peelSide(s, e);
  return exitBottom(
    peel(e, side, 16)
      .steerTo(aimX(s, 48), 150, 56, 4)
      .turnTo(DOWN + side * deg(30), 44)
      .turnTo(DOWN - side * deg(30), 44),
  ).build();
}

/** To the point above the fighter where the beam opens. */
function beamPath(s: GameState, e: Enemy): Path {
  const x = clamp(s.player.x, BEAM.minX, BEAM.maxX);
  const y = FIELD.fighterY - BEAM.above;
  return peel(e, peelSide(s, e), 16).steerTo(x, y - 36, 40, 4).steerTo(x, y, 14, 1).build();
}

function begin(s: GameState, e: Enemy, path: Path, after: AfterPath, d: Difficulty, group: number): void {
  e.state = 'diving';
  e.path = path;
  e.dist = 0;
  e.ox = 0;
  e.oy = 0;
  e.side = 0;
  e.after = after;
  e.group = group;
  e.escorts = 0;
  e.timer = 0;
  e.speed = d.diveSpeed * (isFrenzy(s) ? DIVE.frenzySpeed : 1);
  e.bombs = d.bombsPerDiver;
  e.bombTimer = BOMB.firstMin + s.rng.int(BOMB.firstMax - BOMB.firstMin + 1);
}

function announce(s: GameState, e: Enemy): void {
  s.events.push({ type: 'dive', x: e.x, y: e.y, kind: e.kind });
}

export function launchStinger(s: GameState, e: Enemy, d: Difficulty): void {
  begin(s, e, stingerPath(s, e), 'home', d, nextGroup(s));
  announce(s, e);
}

/** A Moth, or a rogue captive, which flies as one and does not come back. */
export function launchMoth(s: GameState, e: Enemy, d: Difficulty): void {
  begin(s, e, mothPath(s, e), e.kind === 'captive' ? 'leave' : 'wrap', d, nextGroup(s));
  announce(s, e);
}

/** Fly `follower` on `leader`'s path, keeping the place it has beside it now. */
function follow(s: GameState, follower: Enemy, leader: Enemy, path: Path, d: Difficulty): void {
  const ox = follower.x - leader.x;
  const oy = follower.y - leader.y;
  begin(s, follower, path, 'wrap', d, leader.group);
  follower.ox = ox;
  follower.oy = oy;
}

/** A Monarch: a beam run, or a dive with whichever Moths beneath it are home, and its captive. */
export function launchMonarch(s: GameState, e: Enemy, d: Difficulty, beamRun: boolean): void {
  if (beamRun) {
    begin(s, e, beamPath(s, e), 'beam', d, nextGroup(s));
    e.bombs = 0;
    announce(s, e);
    return;
  }
  const path = monarchPath(s, e);
  begin(s, e, path, 'wrap', d, nextGroup(s));
  let escorts = 0;
  for (const m of s.enemies) {
    if (escorts >= 2) break;
    if (m.kind !== 'moth' || m.state !== 'formed' || m.row !== 1 || Math.abs(m.col - e.col) > 1) continue;
    follow(s, m, e, path, d);
    escorts++;
  }
  e.escorts = escorts;
  for (const c of s.enemies) {
    if (c.kind === 'captive' && c.partner === e.id && c.state === 'formed') {
      follow(s, c, e, path, d);
      c.bombs = 0;
    }
  }
  announce(s, e);
}

/** An enemy with no slot turns on the fighter from wherever it is, and leaves by the bottom. */
export function attackFrom(s: GameState, e: Enemy, d: Difficulty): void {
  const path = exitBottom(new PathBuilder(e.x, e.y, e.heading).steerTo(aimX(s), 200, 40, 4)).build();
  begin(s, e, path, 'leave', d, 0);
}

/** One of the Empress's swarm: out of her side and straight at the fighter. */
export function launchSwarmer(s: GameState, x: number, y: number, d: Difficulty): void {
  const e = makeEnemy(s, 'stinger', x, y);
  e.heading = DOWN;
  attackFrom(s, e, d);
  s.enemies.push(e);
  announce(s, e);
}

/** A Stinger begins to change (02 §5). */
export function startMorph(s: GameState, e: Enemy): void {
  e.state = 'morphing';
  e.timer = 0;
  s.events.push({ type: 'morph', x: e.x, y: e.y });
}

/** The morph type in play at `stage`: it changes every four stages. */
export function morphType(stage: number): number {
  return Math.floor(Math.max(0, stage - DIVE.morphFrom) / 4) % 3;
}

/** The flashing Stinger becomes three, which dive nose to tail. */
export function becomeTrio(s: GameState, e: Enemy, d: Difficulty): void {
  const type = morphType(s.stage);
  const trio = s.director.nextTrio++;
  const path = mothPath(s, e);
  e.kind = 'morph';
  e.variant = type;
  e.trio = trio;
  begin(s, e, path, 'wrap', d, nextGroup(s));
  for (let k = 1; k <= 2; k++) {
    const m = makeEnemy(s, 'morph', e.x, e.y);
    m.variant = type;
    m.trio = trio;
    begin(s, m, path, 'leave', d, e.group);
    // It waits at the start of the path until the one in front is clear.
    m.dist = -k * DIVE.trioGap;
    m.bombs = 0;
    s.enemies.push(m);
  }
  announce(s, e);
}
